'use client';

import { useEffect, useState, useTransition } from 'react';

import { useRouter } from 'next/navigation';

import {
  ProfileWizard,
  skipWizardAction,
} from '@kit/accounts/professional-profile';

// Re-export TrialStatusBar from shared component
export { AccountStatusBar } from '~/components/account-status-bar';
export type { AccountStatusBarProps } from '~/components/account-status-bar';

interface ProfileWizardWrapperProps {
  userId: string;
  hasProfile: boolean;
  wizardSkipped: boolean;
}

const WIZARD_SKIPPED_KEY = 'wizard_skipped';

export function ProfileWizardWrapper({
  userId,
  hasProfile,
  wizardSkipped,
}: ProfileWizardWrapperProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [wizardOpen, setWizardOpen] = useState(false);
  // Simple flag to track if user skipped in this session
  const [sessionSkipped, setSessionSkipped] = useState(false);

  // On mount, check localStorage as a backup for the skipped state
  useEffect(() => {
    if (localStorage.getItem(WIZARD_SKIPPED_KEY) === 'true') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Needed for hydration from localStorage
      setSessionSkipped(true);
    }
  }, []);

  // Combined skipped state
  const isSkipped = wizardSkipped || sessionSkipped;

  // Listen for custom event to open wizard from header button
  useEffect(() => {
    const handleOpenWizardEvent = () => {
      setWizardOpen(true);
    };

    window.addEventListener('openTrialWizard', handleOpenWizardEvent);
    return () => {
      window.removeEventListener('openTrialWizard', handleOpenWizardEvent);
    };
  }, []);

  const handleComplete = () => {
    setWizardOpen(false);
    router.refresh();
  };

  const handleSkip = () => {
    // 1. Immediately hide wizard via local state
    setSessionSkipped(true);
    setWizardOpen(false);

    // 2. Save to localStorage as backup
    localStorage.setItem(WIZARD_SKIPPED_KEY, 'true');

    // 3. Update database and refresh
    startTransition(async () => {
      const result = await skipWizardAction({});

      if (!result.success) {
        console.error('Failed to save skip to database:', result.error);
      }

      router.refresh();
    });
  };

  // Show wizard if user doesn't have a profile yet and hasn't skipped, or if manually opened
  if ((!hasProfile && !isSkipped) || wizardOpen) {
    return (
      <ProfileWizard
        open={!isPending}
        userId={userId}
        onComplete={handleComplete}
        onSkip={handleSkip}
      />
    );
  }

  return null;
}
